import "ag-grid-enterprise";
import { z } from "zod";
import React, { useRef, useState, useCallback } from "react";
import { useWindowSize } from "@uidotdev/usehooks";
import { AgGridReact } from "ag-grid-react"; // React Grid
import { useHotkeys } from "react-hotkeys-hook";
import { Pencil } from "lucide-react";

import { Breadcrumbs } from "../../../Components/Breadcrumbs";
import { useParams } from "../../../Hooks/useNavigation";
import useForm from "../../../Hooks/useForm";
import Grid from "../../../Components/Grid";
import Spacer from "../../../Components/Spacer";
import { Text } from "../../../Components/Text";
import Button from "../../../Components/Button";
import { Hotkey } from "../../../Components/Hotkey";
import { AutoGrowTextArea } from "../../../Components/Inputs/AutoGrowTextArea";

// local imports

import { useDecisionLogData } from "./hooks/useDecisionLogData";
import { useDecisionLogActions } from "./hooks/useDecisionLogActions";
import { CreateDecisionModal } from "./components/CreateDecisionModal";
import { EditProjectModal } from "./components/EditProjectModal";
import { getColumnDefs, getContextMenuItems, getStatusBar } from "./gridConfig";

import css from "./styles.styl";
import "ag-grid-community/styles/ag-grid.css"; // Core CSS
import "ag-grid-community/styles/ag-theme-quartz.css"; // Theme

const DEFAULT_COLUMN_DEF = {
	sortable: true,
	filter: true,
	floatingFilter: true,
};

const DecisionLog = () => {
	const gridRef = useRef(null);
	const textAreaRef = useRef(null);

	const size = useWindowSize();
	const { projectId } = useParams();

	const [query, setQuery] = useState("");
	const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [isViewingDecisionLog, setIsViewingDecisionLog] = useState(false);

	const decisionFormContext = useForm({
		schema: {
			decision: z.string().min(1),
		},
		defaultValues: {
			decision: "",
		},
	});

	const projectFormContext = useForm({
		schema: {
			name: z.string().min(1),
			description: z.string().min(1),
		},
	});

	const {
		decisions,
		project,
		decisionLogsStream,
		isLoadingDecisions,
		refetchDecisions,
		refetchProject,
		queryDecisionLogs,
	} = useDecisionLogData(projectId, projectFormContext);

	const {
		createDecision,
		updateDecision,
		deleteDecisions,
		updateProject,
		isCreatingDecision,
		isUpdatingDecision,
		isDeletingDecisions,
	} = useDecisionLogActions({
		projectId,
		decisionFormContext,
		setIsCreateModalOpen,
		refetchDecisions,
		refetchProject,
		setIsEditProjectModalOpen,
	});

	useHotkeys("c", (event) => {
		event.preventDefault();
		setIsCreateModalOpen(true);
	});

	useHotkeys("q", (event) => {
		event.preventDefault();
		textAreaRef.current.focus();
	});

	const onCellValueChanged = (params) => {
		const itemId = params.data._id;
		const newValue = params.newValue;

		updateDecision({ decisionId: itemId, newValue });
	};

	const handleQueryDecisionLogs = async () => {
		setIsViewingDecisionLog(true);
		queryDecisionLogs({ query, projectIds: [projectId] });
	};

	const columnDefs = getColumnDefs(updateDecision);
	const statusBar = getStatusBar(isLoadingDecisions, isCreatingDecision, isUpdatingDecision, isDeletingDecisions);

	const handleCloseCreateModal = useCallback(() => {
		setIsCreateModalOpen(false);
	}, []);

	const handleCloseEditProjectModal = useCallback(() => {
		setIsEditProjectModalOpen(false);
	}, []);

	return (
		<div className={css.container}>
			<Spacer size={2} />

			<CreateDecisionModal
				isOpen={isCreateModalOpen}
				onClose={handleCloseCreateModal}
				onSubmit={() => createDecision(decisionFormContext.state.decision)}
				decision={decisionFormContext}
			/>

			<EditProjectModal
				isOpen={isEditProjectModalOpen}
				onClose={handleCloseEditProjectModal}
				onSubmit={() =>
					updateProject({
						name: projectFormContext.state.name,
						description: projectFormContext.state.description,
					})
				}
				project={projectFormContext}
			/>

			<Breadcrumbs
				crumbs={[
					{
						label: "Projects",
						href: "/decision-log",
					},
					{
						label: (
							<div className={css.breadcrumbItem}>
								<span>{project?.name}</span> <Pencil size={12} />
							</div>
						),
						onClick: () => setIsEditProjectModalOpen(true),
					},
				]}
			/>

			<Spacer size={2} />

			<Grid columns={["1fr", "200px", "100px"]}>
				<div>
					<Text size={2} weight="bold" as="label">
						Ask a question:
					</Text>
					<AutoGrowTextArea
						ref={textAreaRef}
						value={query}
						onChange={(value) => setQuery(value)}
						onEnter={handleQueryDecisionLogs}
					/>
				</div>

				<div />

				<div>
					<Button className={css.createButton} onClick={() => setIsCreateModalOpen(true)}>
						<Hotkey>C</Hotkey>reate
					</Button>
				</div>
			</Grid>

			<Spacer size={2} />

			<Grid columns={isViewingDecisionLog ? ["1fr", "360px"] : ["1fr"]} gap={24}>
				<div className={css.gridContainer}>
					<div className="ag-theme-quartz" style={{ height: size.height - 200 }}>
						<AgGridReact
							ref={gridRef}
							animateRows={false}
							defaultColDef={DEFAULT_COLUMN_DEF}
							rowData={decisions || []}
							columnDefs={columnDefs}
							statusBar={statusBar}
							enableRangeSelection={true}
							rowSelection="multiple"
							overlayLoadingTemplate={
								'<div aria-live="polite" aria-atomic="true" style="height:100px; width:100px; background: url(https://ag-grid.com/images/ag-grid-loading-spinner.svg) center / contain no-repeat; margin: 0 auto;" aria-label="loading"></div>'
							}
							onCellValueChanged={onCellValueChanged}
							getContextMenuItems={(params) => getContextMenuItems(params, gridRef, deleteDecisions)}
						/>
					</div>
				</div>

				{isViewingDecisionLog ? (
					<div className={css.responseContainer}>
						<div className={css.streamResponse} dangerouslySetInnerHTML={{ __html: decisionLogsStream }} />
					</div>
				) : null}
			</Grid>
		</div>
	);
};

export default DecisionLog;
